import axios from 'axios';
import { BACKEND_KEYS } from '../../common/consts/app-keys.const';
import { HttpFactoryService, HttpService } from '../../common/services';
import {
  IActivateAccount,
  ISignin,
  ISignup,
  IForgotPassword,
  ICheckRecoveryCode,
  IResetPassword
} from '../types';

class AuthService {
  httpService: HttpService;

  constructor(private httpFactory: HttpFactoryService) {
    this.httpService = httpFactory.createHttpService();
  }

  public async login(payload: ISignin) {
    const result = await this.httpService.post<{ user: { email: string } }, ISignin>(
      BACKEND_KEYS.SIGNIN,
      payload
    );
    if (!result?.user?.email) throw new Error('Error while signing in');
    return result;
  }

  public async signup(payload: ISignup) {
    const result: true | void = await this.httpService.post(
      BACKEND_KEYS.SIGNUP,
      payload
    );
    if (result !== true) throw new Error('Error while signing up');
    return result;
  }

  public async activateAccount(payload: IActivateAccount, trial?: string) {
    const url = trial
      ? `${BACKEND_KEYS.ACTIVATE_ACCOUNT}?trial=${trial}`
      : BACKEND_KEYS.ACTIVATE_ACCOUNT;
    const result = await this.httpService.post<
      { user: { email: string } },
      { token: string }
    >(url, { token: payload.code });
    if (!result?.user?.email) throw new Error('Error while activating account');
    return result;
  }

  public async forgotPassword(payload: IForgotPassword) {
    const result: true | void = await this.httpService.post(
      BACKEND_KEYS.FORGOT_PASSWORD,
      payload
    );
    if (result !== true) throw new Error('Error while sending recovery code');
    return payload.email;
  }

  public async checkRecoveryCode(payload: ICheckRecoveryCode) {
    const result: true | void = await this.httpService.post(
      BACKEND_KEYS.CHECK_RECOVERY_CODE,
      payload
    );
    if (result !== true) throw new Error('Error while checking recovery code');
    return payload.token;
  }

  public async resetPassword(payload: IResetPassword) {
    const result = await this.httpService.post(BACKEND_KEYS.PASSWORD_RESET, payload);
    if (!result) throw new Error('Error while resetting password');
    return result;
  }

  public async resendRecoveryCode(payload: IForgotPassword) {
    const result: true | void = await this.httpService.post(
      BACKEND_KEYS.RESEND_RECOVERY_CODE,
      payload
    );
    return payload.email;
  }

  public async resendActivationCode(payload: IForgotPassword) {
    const result: true | void = await this.httpService.post(
      BACKEND_KEYS.RESEND_ACTIVATION_CODE,
      payload
    );
    return payload.email;
  }

  public async getMe(): Promise<{
    user: {
      id: number;
      email: string;
      isAdmin?: boolean;
    };
  } | null> {
    try {
      const baseUrl =
        process.env.NODE_ENV === 'development'
          ? `${process.env.REACT_APP_BACKEND_URL_DEV}/api/v1`
          : `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
      const result = await axios.get<{
        user: {
          id: number;
          email: string;
          isAdmin?: boolean;
        };
      }>(`${baseUrl}/${BACKEND_KEYS.ME}`, { withCredentials: true });
      return result.data ?? null;
    } catch {
      return null;
    }
  }

  public async logout(): Promise<void> {
    await this.httpService.post(BACKEND_KEYS.LOGOUT, {});
  }
}

const authService = new AuthService(new HttpFactoryService());

export { authService };
